import React, { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { Autocomplete, TextField } from '@mui/material';

import shipService, { Ship } from '~/services/cruises/ShipService';

interface Props {
  cruiseLineName?: string;
  shipIds: Array<string>;
  onChange: (shipIds: Array<string>) => void;
  disabled?: boolean;
}

const ITEMS_PER_PAGE = 1000;

function InputShipMultiselect({ cruiseLineName, shipIds, onChange, disabled }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const { data: ships = [], isLoading } = useQuery({
    queryKey: ['cruise', 'ship-multiselect', cruiseLineName],
    queryFn: async () => {
      try {
        const response = await shipService.listWithPagination({
          take: ITEMS_PER_PAGE,
          cruiseLine: cruiseLineName,
        });
        return response.result;
      } catch (error) {
        enqueueSnackbar('There was an error while fetching ships', { variant: 'error' });
        throw error;
      }
    },
    enabled: !!cruiseLineName,
  });

  const selectedShips = useMemo(() => ships.filter((ship) => shipIds.includes(ship.id)), [ships, shipIds]);

  const handleChange = useCallback(
    (_: unknown, selectedShips: Array<Ship>) => onChange(selectedShips.map((ship) => ship.id)),
    [onChange],
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={ships}
      value={selectedShips}
      loading={isLoading}
      disabled={disabled || !cruiseLineName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      onChange={handleChange}
      renderInput={(params) => <TextField {...params} fullWidth label="Ships" placeholder="Select ships" />}
    />
  );
}

export default InputShipMultiselect;
